import { useEffect, useState } from 'react'
import { UserCheck, RotateCcw, UserX, AlertTriangle, LogOut } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Label } from '../../lib/shadcn/label'
import { Input } from '../../lib/shadcn/input'
import { Textarea } from '../../lib/shadcn/textarea'
import { Spinner } from '../../lib/shadcn/spinner'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../lib/shadcn/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../lib/shadcn/alert-dialog'
import { toast } from '../../lib/shadcn/sonner'
import { Chip } from './Chip'
import { SuspensionLetterAction } from './SuspensionLetterAction'
import {
  useUpdateApplicationStatus,
  useRecordResignation,
  useGetResignation,
} from '../../hooks/backend/interviews'
import { statusLabel, statusTone } from '../../utils/interviews'

// The three interchangeable states an active driver can hold.
const STATES = [
  { code: 'active', label: 'Active' },
  { code: 'compliance', label: 'Compliance' },
  { code: 'suspended', label: 'Suspended' },
] as const

type Resignation = {
  resigned_on: string
  reason: string
  recorded_at: string
  recorded_by_name: string | null
  is_current: boolean
} | null

// Today in Johannesburg as YYYY-MM-DD. A resignation cannot be dated after it,
// and the browser's own clock may be in another zone.
function todaySast(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Johannesburg' }).format(new Date())
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// A date column read as its own digits. Parsing it with Date would place it at
// UTC midnight and let a timezone move it a day.
function dayLabel(iso: string | null | undefined): string {
  const m = iso ? /^(\d{4})-(\d{2})-(\d{2})/.exec(iso) : null
  if (!m) return 'Not recorded'
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}`
}

function sastDay(ts: string | null | undefined): string {
  if (!ts) return 'Not recorded'
  return dayLabel(new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Johannesburg' }).format(new Date(ts)))
}

export function DriverStateControl({
  applicationId,
  currentStatus,
  canAct,
  canEditDrivers = false,
  onSaved,
}: {
  applicationId: string
  currentStatus: string
  canAct: boolean
  canEditDrivers?: boolean
  onSaved: () => void
}) {
  const { trigger } = useUpdateApplicationStatus()
  const recordResignation = useRecordResignation()
  const resignation = useGetResignation<Resignation>()
  const resignationTrigger = resignation.trigger
  const [choice, setChoice] = useState(currentStatus)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [dismissOpen, setDismissOpen] = useState(false)
  const [resignOpen, setResignOpen] = useState(false)
  const [resignReason, setResignReason] = useState('')
  const [resignDate, setResignDate] = useState('')

  // Reset the picker whenever the applicant or their state changes.
  useEffect(() => {
    setChoice(currentStatus)
    setReason('')
    setResignReason('')
    setResignDate('')
  }, [applicationId, currentStatus])

  const isResigned = currentStatus === 'resigned'

  useEffect(() => {
    if (isResigned) resignationTrigger({ applicationId }, { skipCache: true })
  }, [isResigned, applicationId, resignationTrigger])

  const changed = choice !== currentStatus
  const isSuspended = currentStatus === 'suspended'
  const isDismissed = currentStatus === 'dismissed'
  // Held while an offboarding case is open (db/migrations/1711).
  const isOffboarding = currentStatus === 'offboarding'
  const today = todaySast()
  const resignReady = resignReason.trim().length > 0 && resignDate !== '' && resignDate <= today

  const runTransition = async (newStatus: string, successMsg: string) => {
    const trimmed = reason.trim()
    setBusy(true)
    try {
      await trigger({
        application_id: applicationId,
        new_status: newStatus,
        reason: trimmed.length ? trimmed : null,
        send_email: false,
      }).result
      toast.success(successMsg)
      setReason('')
      onSaved()
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      toast.error('Could not update driver', { description: msg })
    } finally {
      setBusy(false)
    }
  }

  const handleSave = async () => {
    if (!changed) return
    await runTransition(choice, `Driver state changed to ${statusLabel(choice)}`)
  }

  const handleReinstate = async () => {
    await runTransition('active', 'Driver reinstated to Active')
  }

  const handleDismiss = async () => {
    if (!reason.trim()) return
    setDismissOpen(false)
    await runTransition('dismissed', 'Driver dismissed')
  }

  const handleResign = async () => {
    if (!resignReady) return
    setBusy(true)
    try {
      await recordResignation.trigger({
        application_id: applicationId,
        reason: resignReason.trim(),
        resigned_on: resignDate,
      }).result
      toast.success(`Resignation recorded, dated ${dayLabel(resignDate)}`)
      setResignOpen(false)
      setResignReason('')
      setResignDate('')
      onSaved()
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      toast.error('Could not record the resignation', { description: msg })
    } finally {
      setBusy(false)
    }
  }

  // Resignation is an employment record, so it needs Edit drivers (the database
  // checks the same thing). Without it the button is replaced by the sentence that
  // tells the person how to get it, rather than a button that would refuse.
  const resignButton = (label: string) =>
    canEditDrivers ? (
      <Button
        variant="outline"
        onClick={() => setResignOpen(true)}
        disabled={busy}
        className="h-11 justify-center sm:h-9"
      >
        <LogOut className="h-4 w-4" />
        {label}
      </Button>
    ) : null

  const resignNeedsAccess = !canEditDrivers && (
    <p className="text-[11px] text-muted-foreground">
      Recording a resignation needs Edit drivers in cockpit access. Ask an admin to switch it on for you.
    </p>
  )

  const r = resignation.data
  const reinstateBlock = (placeholder: string) => (
    <>
      <div className="space-y-1.5">
        <Label
          htmlFor={`state-reason-${applicationId}`}
          className="text-xs font-medium text-muted-foreground"
        >
          Reinstate note (optional)
        </Label>
        <Textarea
          id={`state-reason-${applicationId}`}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={placeholder}
          disabled={busy}
          rows={2}
        />
      </div>

      <Button
        onClick={handleReinstate}
        disabled={busy}
        className="h-11 w-full justify-center sm:h-9 sm:w-auto"
      >
        {busy ? <Spinner className="h-4 w-4" /> : <RotateCcw className="h-4 w-4" />}
        Reinstate to Active
      </Button>
    </>
  )

  return (
    <section className="rounded-lg border border-border bg-card p-4">
      <div className="mb-3 flex items-center gap-2">
        <UserCheck className="h-4 w-4 text-[hsl(var(--brand))]" />
        <h3 className="text-sm font-semibold text-foreground">Driver state</h3>
      </div>

      <div className="mb-3 flex items-center gap-2">
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Current</span>
        <Chip tone={statusTone(currentStatus)}>{statusLabel(currentStatus)}</Chip>
      </div>

      {!canAct ? (
        <p className="text-xs text-muted-foreground">
          You do not have permission to change this driver's state.
        </p>
      ) : isResigned ? (
        <div className="space-y-3">
          <div className="rounded-md border border-border bg-muted/40 p-3 text-sm text-foreground">
            {resignation.loading && !r ? (
              <span className="flex items-center gap-2 text-muted-foreground">
                <Spinner className="h-4 w-4" /> Reading the resignation
              </span>
            ) : resignation.error ? (
              <span>
                This driver resigned. The resignation date could not be read just now: {resignation.error}
              </span>
            ) : r && r.is_current ? (
              <div className="space-y-1">
                <p className="font-medium">This driver resigned on {dayLabel(r.resigned_on)}</p>
                <p className="text-muted-foreground">{r.reason}</p>
                <p className="text-[11px] text-muted-foreground">
                  Recorded by {r.recorded_by_name ?? 'a person whose name is not on file'} on {sastDay(r.recorded_at)}
                </p>
              </div>
            ) : (
              <span>
                This driver resigned. The resignation date was not recorded, because the status was set before the
                Cockpit could record one.
              </span>
            )}
          </div>

          {reinstateBlock('Why is this driver coming back?')}
        </div>
      ) : isOffboarding ? (
        <div className="space-y-3">
          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              This driver is being offboarded, so they are out of the Active list until the exit is confirmed. The
              driver record stays active to the last working day so shifts can still be submitted. The case is in the
              Offboarding panel below.
            </span>
          </div>

          <div className="space-y-2">
            <p className="text-[11px] text-muted-foreground">
              Recording the resignation confirms the exit. For a dismissal, confirm it on the offboarding case.
            </p>
            {resignButton('Confirm the resignation')}
            {resignNeedsAccess}
          </div>

          <div className="space-y-2 border-t border-border pt-3">
            <p className="text-[11px] text-muted-foreground">
              If the driver is staying after all, reinstating them cancels the offboarding case.
            </p>
            {reinstateBlock('Why is this driver staying?')}
          </div>
        </div>
      ) : isDismissed ? (
        <div className="space-y-3">
          <div className="flex items-start gap-2 rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-200">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>This driver has been dismissed</span>
          </div>

          {reinstateBlock('Why is this driver being reinstated?')}

          <div className="space-y-2 border-t border-border pt-3">
            <p className="text-[11px] text-muted-foreground">
              If the driver resigned and was filed as dismissed, record the resignation with its date. The dismissal
              stays in the history.
            </p>
            {resignButton('Record as resigned instead')}
            {resignNeedsAccess}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor={`state-${applicationId}`} className="text-xs font-medium text-muted-foreground">
              State
            </Label>
            <Select value={choice} onValueChange={setChoice} disabled={busy}>
              <SelectTrigger id={`state-${applicationId}`} className="w-full sm:max-w-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATES.map((s) => (
                  <SelectItem key={s.code} value={s.code}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label
              htmlFor={`state-reason-${applicationId}`}
              className="text-xs font-medium text-muted-foreground"
            >
              Reason (optional)
            </Label>
            <Textarea
              id={`state-reason-${applicationId}`}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is the state changing?"
              disabled={busy}
              rows={2}
            />
          </div>

          <Button
            onClick={handleSave}
            disabled={!changed || busy}
            className="h-11 w-full justify-center sm:h-9 sm:w-auto"
          >
            {busy && <Spinner className="h-4 w-4" />}
            Save state
          </Button>

          <p className="text-xs text-muted-foreground">
            Active, Compliance and Suspended are fully reversible. No email is sent for these changes: suspending
            does not tell the driver anything. Once he is Suspended, use Issue suspension letter below to write to
            him.
          </p>

          {!isSuspended && (
            <div className="space-y-2 border-t border-border pt-3">
              {resignButton('Driver resigned')}
              {resignNeedsAccess}
            </div>
          )}

          {isSuspended && (
            <div className="space-y-3 rounded-md border border-border bg-muted/30 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Suspended: resolve
              </p>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button
                  onClick={handleReinstate}
                  disabled={busy}
                  className="h-11 justify-center sm:h-9"
                >
                  {busy ? <Spinner className="h-4 w-4" /> : <RotateCcw className="h-4 w-4" />}
                  Reinstate to Active
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => setDismissOpen(true)}
                  disabled={busy}
                  className="h-11 justify-center sm:h-9"
                >
                  <UserX className="h-4 w-4" />
                  Dismiss driver
                </Button>
                {resignButton('Driver resigned')}
              </div>
              <p className="text-[11px] text-muted-foreground">
                Dismissing requires a reason. Add it in the box above before confirming. A resignation asks for its
                own reason and date.
              </p>
              {resignNeedsAccess}

              <div className="border-t border-border pt-3">
                <SuspensionLetterAction applicationId={applicationId} onSaved={onSaved} />
                <p className="mt-1.5 text-[11px] text-muted-foreground">
                  Separate from the state change above. Nothing here is sent automatically; a letter goes
                  out only after you review a preview and confirm.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      <AlertDialog open={dismissOpen} onOpenChange={setDismissOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Dismiss this driver?</AlertDialogTitle>
            <AlertDialogDescription>
              This ends their engagement. You can reinstate later. Dismissing sends nothing to the driver: no
              email and no letter. If the driver must be told in writing, do that separately and check that
              it arrived.
              {!reason.trim() && (
                <span className="mt-2 block font-medium text-destructive">
                  Add a reason in the box before dismissing.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                handleDismiss()
              }}
              disabled={busy || !reason.trim()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Dismiss driver
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={resignOpen} onOpenChange={(o) => !busy && setResignOpen(o)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Record that this driver resigned</AlertDialogTitle>
            <AlertDialogDescription>
              This moves the driver record to Resigned and takes them off the car allocation. No email is sent. You can
              reinstate them later if they come back.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor={`resign-date-${applicationId}`} className="text-xs font-medium text-muted-foreground">
                Date the driver resigned
              </Label>
              <Input
                id={`resign-date-${applicationId}`}
                type="date"
                value={resignDate}
                max={today}
                onChange={(e) => setResignDate(e.target.value)}
                disabled={busy}
                className="w-full sm:max-w-[12rem]"
              />
              <p className="text-[11px] text-muted-foreground">
                The day they gave notice, from their letter or message. It is the date a certificate of service states.
              </p>
              {resignDate > today && (
                <p className="text-[11px] font-medium text-destructive">
                  That date is after today. Record a resignation on or after the day the driver gives it.
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor={`resign-reason-${applicationId}`} className="text-xs font-medium text-muted-foreground">
                Reason
              </Label>
              <Textarea
                id={`resign-reason-${applicationId}`}
                value={resignReason}
                onChange={(e) => setResignReason(e.target.value)}
                placeholder="Why did the driver resign? Their own words if you have them."
                disabled={busy}
                rows={3}
              />
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                handleResign()
              }}
              disabled={busy || !resignReady}
            >
              {busy && <Spinner className="h-4 w-4" />}
              Record resignation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
}
